import { useState, useEffect, useRef } from 'react';
import { ArrowLeft, Plus, Trash2, Image as ImageIcon, ExternalLink } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function CmsGalerie({ navigate }) {
  const [section, setSection] = useState('restaurant');
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => fetch(`${API}/api/site/gallery?section=${section}`).then(r => r.json()).then(setPhotos).catch(() => {});
  useEffect(() => { load(); }, [section]);

  const handleUpload = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    for (const file of files) {
      const form = new FormData();
      form.append('file', file);
      form.append('section', section);
      try {
        await fetch(`${API}/api/site/gallery/upload`, { method: 'POST', body: form });
      } catch (e) { console.error(e); }
    }
    setUploading(false);
    load();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette photo ?')) return;
    await fetch(`${API}/api/site/gallery/${id}`, { method: 'DELETE' });
    load();
  };

  return (
    <div data-testid="cms-galerie">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('dashboard')} className="flex items-center gap-2 text-gray-500 hover:text-gray-700 text-sm" data-testid="galerie-back-btn">
            <ArrowLeft size={16} /> Retour au site
          </button>
          <div className="flex items-center gap-2">
            <span className="text-gray-300">|</span>
            <h1 className="text-lg font-bold text-gray-900">Administration - Galerie Photos</h1>
          </div>
        </div>
        <div className="flex items-center gap-4 text-sm text-gray-500">
          <button onClick={() => navigate('settings')} className="hover:text-gray-700">Parametres</button>
          <button onClick={() => navigate('cartes')} className="hover:text-gray-700">Carte</button>
          <button onClick={() => navigate('plateaux')} className="hover:text-gray-700">Plateaux</button>
        </div>
      </div>

      {/* Section toggle + add button */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex rounded-lg overflow-hidden border border-gray-200">
          <button onClick={() => setSection('restaurant')}
            className={`px-5 py-2 text-sm font-medium transition-colors ${section === 'restaurant' ? 'bg-[#1a2744] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
            data-testid="galerie-tab-restaurant">
            Restaurant
          </button>
          <button onClick={() => setSection('traiteur')}
            className={`px-5 py-2 text-sm font-medium transition-colors ${section === 'traiteur' ? 'bg-[#1a2744] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
            data-testid="galerie-tab-traiteur">
            Traiteur
          </button>
        </div>
        <button onClick={() => fileRef.current?.click()}
          className="flex items-center gap-2 bg-[#1a2744] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#243559]"
          data-testid="add-image-btn">
          <Plus size={16} /> Ajouter une image
        </button>
        <input ref={fileRef} type="file" accept="image/*" multiple className="hidden"
          onChange={e => { handleUpload(Array.from(e.target.files)); e.target.value = ''; }} />
      </div>

      {/* Gallery grid */}
      <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
        {uploading && (
          <div className="flex items-center justify-center py-4 mb-4">
            <div className="animate-spin w-6 h-6 border-2 border-gray-300 border-t-blue-500 rounded-full mr-3" />
            <span className="text-sm text-gray-500">Upload en cours...</span>
          </div>
        )}

        {photos.length === 0 && !uploading && (
          <div className="text-center py-12">
            <ImageIcon size={48} className="text-gray-200 mx-auto mb-3" />
            <p className="text-gray-500">Aucune image dans la galerie</p>
            <p className="text-sm text-gray-400">Ajoutez des images avec le bouton ci-dessus</p>
          </div>
        )}

        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2">
          {photos.map(photo => (
            <div key={photo.id} className="relative group aspect-square rounded-lg overflow-hidden bg-gray-100" data-testid={`photo-${photo.id}`}>
              <img src={photo.url.startsWith('http') ? photo.url : `${API}${photo.url}`}
                alt={photo.caption || ''} className="w-full h-full object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors" />
              <button onClick={() => handleDelete(photo.id)}
                className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`delete-photo-${photo.id}`}>
                <Trash2 size={12} />
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="text-center">
        <a href={`/site/${section}/galerie`} target="_blank" rel="noreferrer"
          className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700">
          <ExternalLink size={14} /> Voir la galerie sur le site
        </a>
      </div>
    </div>
  );
}
